import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';

import '../../../../assets/css/player.css';
import '../../../../features/cards/css/user/feed.css';
import './ViewContent.css';
import { showInfo } from '../../../../shared/utils/swalHandler';
import useNavigation from '../../../../shared/utils/useNavigation';
import { MessageIcon } from '../../../../assets/Icons/SVG';
import {
  formatTimeAgo,
  formatDuration,
  isBundleContent,
  getMediaArray,
  getPrimaryMedia,
  isUserLoggedIn,
  isPostAccessible,
} from '../../../../shared/utils/function';
import {
  DEFAULT_USER_AVATAR,
  MEDIA_TYPE,
  PLACEHOLDER_POST_IMAGE,
  MESSAGE_READ,
  TIP_TYPE
} from '../../../../shared/constants/common';
import BundleSlider from "../../../../features/slider/BundleSlider";
import LazyImage from '../../../../components/LazyImage/LazyImage';
import VideoPlayer from '../../../../shared/components/VideoPlayer';
import GridDropdown from '../../../../features/cards/user/gridDropdown';
import LockedPPVContentPreview from '../../../../features/cards/user/LockedPPVContentPreview';
import LockedMonthlyContent from '../../../../features/cards/user/LockedMonthlyContent';
import TipModal from '../../../Modals/TipModal';
import { useModalOpen } from '../../../../shared/utils/modalUtils.js';


const ViewContent = ({
  postId = null,
  post = null,
  creatorRole = null,
  isCreatorOwnProfile = false,
  creatorInfo = null,
  openDropdownId = null,
  onDropdownToggle = null,
  isShowDuration = false,
  showControls = false,
  isPPVContent = false,
  isSubContent = false,
  isShowChatButton = false,
  isShowEllipsis = false,
  handleClosePostModal,
  posts = [], // Array of all posts
  currentIndex = 0, // Current post index
  onNavigate = null, // Navigation callback function
  onUnlockClick = null,
  onOpenSubscribeModal = null,
  onOpenFollowModal = null,
  vatPercentage = 0,
  // coming from mapStateToProps
  user = null,
}) => {
  const navigate = useNavigation();
  const isLoggedIn = isUserLoggedIn();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [tipModalOpen, setTipModalOpen] = useState(false);
  const [tipModalData, setTipModalData] = useState(null);

  // Reset video playing state when post changes
  useEffect(() => {
    setIsVideoPlaying(false);
  }, [postId, post?._id]);

  // Calculate isSubscribed with safe default for hook usage
  const isSubscribed = post ? isPostAccessible({
    post,
    isPPVContent,
    isSubContent,
    creatorInfo,
  }) : false;

  // Add/remove class on HTML tag when modal opens/closes
  // Only add modal-open class if user is not subscribed and content is PPV or subscription content
  // useModalOpen(!isSubscribed && (isPPVContent || isSubContent));
  useModalOpen(true);


  if (!post) return null;

  // Navigation handlers
  const handlePreviousPost = (e) => {
    e.stopPropagation();
    if (currentIndex > 0 && onNavigate) {
      onNavigate(currentIndex - 1);
    }
  };

  const handleNextPost = (e) => {
    e.stopPropagation();
    if (currentIndex < posts.length - 1 && onNavigate) {
      onNavigate(currentIndex + 1);
    }
  };

  const canNavigatePrevious = currentIndex > 0;
  const canNavigateNext = currentIndex < posts.length - 1;

  const likes = post.tips || 0;
  const isBundle = isBundleContent(post);
  const mediaArray = getMediaArray(post);
  const primaryMedia = getPrimaryMedia(post);

  // Tip functionality functions
  const tipButton = (item) => {
    if (!isLoggedIn) {
      showInfo('', "Log in or Sign up to tip", {
        showCancelButton: true,
        confirmButtonText: 'Login',
      }).then((result) => {
        if (result.isConfirmed) {
          navigate.toLogin();
        }
      });
      return;
    }

    if (!creatorInfo) {
      return;
    }

    setTipModalData({
      creatorInfo: creatorInfo,
      tip_type: TIP_TYPE.CONTENT,
      item: item || post,
      issubscribed: isSubscribed,
    });
    setTipModalOpen(true);
  };

  const toggleTipModal = () => {
    setTipModalOpen((prevOpen) => {
      if (prevOpen) {
        // Clear data after a short delay to allow modal close animation
        setTimeout(() => {
          setTipModalData(null);
        }, 300);
      }
      return !prevOpen;
    });
  };

  const CaptionWithToggle = ({ text, maxLength = 100 }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!text) return null;

    const toggleReadMore = () => setIsExpanded(!isExpanded);

    return (
      <span className="caption-text">
        {isExpanded ? text : text.slice(0, maxLength) + (text.length > maxLength ? '...' : '')}
        {text.length > maxLength && (
          <span onClick={toggleReadMore} className="read-more-btn">
            {isExpanded ? MESSAGE_READ.SHOW_LESS_TEXT : MESSAGE_READ.SHOW_MORE_TEXT}
          </span>
        )}
      </span>
    );
  };

  const handleTagUser = (user) => {
    if (user.username) {
      window.location.href = `/${user.username}`;
    }
  };

  const onTipSuccess = (tipMessage) => {
    // Send the tip amount as a message
    // handleSendMessage({ message: tipMessage });
    onUnlockClick(post); // refresh the post
  };

  const modalContent = (
    <div className="post-modal-overlay cm_bg_blur">
      <div className="post-modal-content">
        <div className="modern-post-ui cm_modern_post_card" data-post-id={postId}>
          <div className="cm_modern_post_content">
            <span className="my_time_stemp">
              {formatTimeAgo(post.created_at)}
            </span>
            <span onClick={handleClosePostModal} className="cm_modal_close_btn">
              <i className='fa fa-xmark'></i>
            </span>


            <div className="post_user_info">
              <LazyImage
                src={creatorInfo?.profile_picture || DEFAULT_USER_AVATAR}
                alt={creatorInfo?.username}
                className='cm-post-height'
                onError={(e) => {
                  e.target.src = DEFAULT_USER_AVATAR;
                }}
              />
            </div>

            <div className='cm_modern_post_text'>
              <div className="username-section" onClick={() => navigate.toProfile(creatorInfo?.username)}>
                <h6>@{creatorInfo?.username}</h6>
              </div>
              {post.caption && (
                <div className="cm_post_caption">
                  <CaptionWithToggle
                    text={post.caption}
                    maxLength={100}
                  />
                </div>
              )}

              {post.tag_users && post.tag_users.length > 0 && (
                <div className="tagged-users-section">
                  {post.tag_users.map((user, index) => (
                    <span key={index} className="tag-user" onClick={() => handleTagUser(user)}>
                      @{user.username || user}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="post-content">
            {isSubscribed && (
              <div className="post-media-container">
                {isBundle && mediaArray && mediaArray.length > 0 ? (
                  <BundleSlider
                    media={mediaArray}
                    showControls={showControls}
                    isLock={false}
                    isShowDuration={isShowDuration}
                  />
                ) : primaryMedia.type === MEDIA_TYPE.IMAGE ? (
                  <LazyImage
                    src={primaryMedia.src}
                    alt="Post content"
                    className='cm-post-height post-media'
                    onError={(e) => {
                      e.target.src = PLACEHOLDER_POST_IMAGE;
                    }}
                  />
                ) : (
                  <VideoPlayer
                    src={primaryMedia.src}
                    poster={primaryMedia.poster || null}
                    className='cm-post-height video-no-timeline bundle-slider-media'
                    showControls={showControls}
                    controlsList="nodownload noplaybackrate noremoteplayback"
                    disablePictureInPicture
                    disableRemotePlayback
                    preventContextMenu
                    muted
                    loop
                    playsInline
                    fallbackText='Video unavailable'
                    showDurationBadge={post.type_file === MEDIA_TYPE.VIDEO}
                    onPlayStarted={() => setIsVideoPlaying(true)}
                    onPause={() => setIsVideoPlaying(false)}
                  />
                )}

                {/* if not bundle Video and duration badge */}
                {isShowDuration && post.type_file === MEDIA_TYPE.VIDEO && post.duration && !isVideoPlaying && (
                  <div className={`video_time_badge`}>
                    <i className='fa-solid fa-video'></i>
                    {formatDuration(post.duration)}
                  </div>
                )}
              </div>
            )}

            {!isSubscribed && isPPVContent && (
              <LockedPPVContentPreview
                purchase_type='content'
                key={post?._id ?? post?.id ?? currentIndex}
                isBundle={isBundle}
                mediaArray={mediaArray}
                post={post}
                creatorInfo={creatorInfo}
                onUnlockClick={() => onUnlockClick(post)}
                isOpenFollowModal={() => onOpenFollowModal()}
                vatPercentage={vatPercentage}
              />
            )}

            {!isSubscribed && isSubContent && (
              <LockedMonthlyContent
                key={post?._id ?? post?.id ?? currentIndex}
                isBundle={isBundle}
                mediaArray={mediaArray}
                post={post}
                creatorInfo={creatorInfo}
                onUnlockClick={() => onUnlockClick(post)}
                isOpenSubscribeModal={() => onOpenSubscribeModal()}
              />
            )}

            {/* Navigation Arrows */}
            {posts.length > 1 && (
              <>
                {/* Left Arrow */}
                {canNavigatePrevious && (
                  <button
                    className="post-navigation-arrow post-navigation-arrow-left"
                    onClick={handlePreviousPost}
                    aria-label="Previous post"
                  >
                    <i className="fa-solid fa-chevron-left"></i>
                  </button>
                )}

                {/* Right Arrow */}
                {canNavigateNext && (
                  <button
                    className="post-navigation-arrow post-navigation-arrow-right"
                    onClick={handleNextPost}
                    aria-label="Next post"
                  >
                    <i className="fa-solid fa-chevron-right"></i>
                  </button>
                )}
              </>
            )}

          </div>

          <div className="post-actions">
            <div className="actions-left">
              <div className="actions-group">
                <div className="like-section">
                  <div className="like-icon">
                    <i className='fas fa-heart'></i>
                  </div>
                  <span className="like-count">
                    {likes.toLocaleString()}
                  </span>
                </div>

                <div
                  className="tips-display"
                  onClick={() => tipButton(post)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="tips-icon">
                    <i className="fa-solid fa-dollar-sign tips-icon-text"></i>
                  </div>
                  <span className="tips-amount">
                    Tip
                  </span>
                </div>

              </div>

              {/* Trending icon for trending content & PPV content */}
              {isPPVContent && post.isTrending && post.isTrending === true && (
                <div className="trending-badge">
                  <span className="trending-badge-text">
                    Trending
                  </span>
                  <i className='fas fa-fire'></i>
                </div>
              )}
            </div>



            {isShowEllipsis && isSubscribed && (
              <GridDropdown
                className="cm_media_grid_dropdown"
                iconClass="fa-ellipsis-vertical"
                id={post?._id || null}
                creatorId={creatorInfo?._id || null}
                isOpen={openDropdownId === post?._id}
                onToggle={onDropdownToggle}
                isShowImpersonation={false}
                isSinglePost={true}
                postId={post?._id}
              />
            )}

            {isShowChatButton && creatorInfo?.username && (
              <button
                className="message-button"
                onClick={() => navigate.toChat(creatorInfo?._id)}
              >
                <MessageIcon />
              </button>
            )}
          </div>

          {/* Tip Modal */}
          {tipModalOpen && tipModalData && (
            <TipModal
              isOpen={tipModalOpen}
              toggle={toggleTipModal}
              creatorInfo={tipModalData.creatorInfo}
              tip_type={tipModalData.tip_type || TIP_TYPE.CONTENT}
              item={tipModalData.item}
              issubscribed={tipModalData.issubscribed}
              onTipSuccess={onTipSuccess}
            />
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
};

const mapStateToProps = (state) => {
  return {
    user: state.user?.user,
  };
};

export default connect(mapStateToProps)(ViewContent);
